"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var i18next_browser_languagedetector_1 = __importDefault(require("i18next-browser-languagedetector"));
var changeLocale_1 = __importDefault(require("./changeLocale"));
var locale_1 = require("./locale");
var translationMetadata_1 = require("./utils/translationMetadata");
var query_string_1 = __importDefault(require("query-string"));
var defaultDetectorOptions = {
    // order and from where user language should be detected
    order: ['querystring', 'cookie', 'localStorage', 'navigator'],
    // keys or params to lookup language from
    lookupQuerystring: 'intl',
    lookupLocalStorage: 'i18nextLng',
    lookupCookie: 'dd_language',
    lookupFromPathIndex: 0,
    lookupFromSubdomainIndex: 0,
    // cache user language on
    caches: ['cookie', 'localStorage'],
    // languages to not persist (cookie, localStorage)
    excludeCacheFor: ['cimode'],
};
var QUERY_PARAMS_STORE_KEY = 'i18nUrlOptions';
function i18nAppProvider(i18nextInstance) {
    return {
        initialize: function (options, packageNamespace) {
            var _this = this;
            var customDetector = options.customDetector, _a = options.detectorOptions, detectorOptions = _a === void 0 ? {} : _a, onInit = options.onInit, localeFallbacks = options.localeFallbacks;
            var _b = this.getQueryParamOptions(), _c = _b.enableOptionalLocales, enableOptionalLocales = _c === void 0 ? false : _c, _d = _b.debug, debug = _d === void 0 ? false : _d;
            var languageDetector = this.addDetector(customDetector);
            var resources = {};
            if ('supportedLocales' in options) {
                var _e = options, supportedLocales = _e.supportedLocales, optionalLocales = _e.optionalLocales, translationFiles = _e.translationFiles;
                var allLocales = enableOptionalLocales ? supportedLocales.concat(optionalLocales) : supportedLocales;
                resources = this.generateResourcesForLocales(allLocales, translationFiles, packageNamespace);
            }
            else if ('localizedStrings' in options) {
                var localizedStrings = options.localizedStrings;
                resources = this.generateResourcesFromStrings(localizedStrings);
            }
            i18nextInstance
                .use(languageDetector)
                .init({
                resources: resources,
                debug: debug,
                detection: __assign(__assign({}, defaultDetectorOptions), detectorOptions),
                fallbackLng: __assign(__assign({}, (localeFallbacks ? localeFallbacks : {})), { default: [locale_1.Locale.EN_US, locale_1.Locale.EN] }),
                interpolation: {
                    escapeValue: false, // not needed for react as it escapes by default
                },
            }, function (err, translate) {
                _this.saveQueryParamOptions({ enableOptionalLocales: enableOptionalLocales, debug: debug });
                onInit && onInit(err, translate);
            });
        },
        getQueryParamOptions: function () {
            if (typeof window === 'undefined')
                return { enableOptionalLocales: false, debug: false };
            var _a = query_string_1.default.parse(location && location.search, { parseBooleans: true }) || {}, enableOptionalLocales = _a.enableOptionalLocales, debug = _a.debug;
            var savedOptions = JSON.parse(sessionStorage.getItem(QUERY_PARAMS_STORE_KEY)) || {};
            return {
                enableOptionalLocales: typeof enableOptionalLocales === 'boolean' ? enableOptionalLocales : savedOptions.enableOptionalLocales,
                debug: typeof debug === 'boolean' ? debug : savedOptions.debug,
            };
        },
        saveQueryParamOptions: function (queryParamOptions) {
            if (typeof window === 'undefined')
                return;
            var enableOptionalLocales = queryParamOptions.enableOptionalLocales, debug = queryParamOptions.debug;
            sessionStorage.setItem(QUERY_PARAMS_STORE_KEY, JSON.stringify({
                enableOptionalLocales: enableOptionalLocales,
                debug: debug
            }));
        },
        addDetector: function (customDetector) {
            var languageDetector = new i18next_browser_languagedetector_1.default();
            if (customDetector) {
                languageDetector.addDetector(customDetector);
            }
            return languageDetector;
        },
        generateResourcesForLocales: function (locales, filenameMap, packageNamespace) {
            var _this = this;
            return locales.reduce(function (acc, locale) {
                var localeTranslationFile = _this.getTranslationFileForLocale(filenameMap[locale], packageNamespace);
                if (!localeTranslationFile)
                    return acc;
                acc[locale] = {
                    translation: localeTranslationFile
                };
                return acc;
            }, {});
        },
        getTranslationFileForLocale: function (filename, packageNamespace) {
            var localeTranslationFile;
            if (!filename)
                return;
            if (packageNamespace) {
                try {
                    localeTranslationFile = require((process.env.TRANSLATIONS_ROOT || process.env.REACT_APP_TRANSLATIONS_ROOT) + "/" + packageNamespace + "/" + filename);
                }
                catch (e) {
                    console.warn("Failed to load translation file for namespace " + packageNamespace + " at path:\n             " + (process.env.TRANSLATIONS_ROOT || process.env.REACT_APP_TRANSLATIONS_ROOT) + "/" + packageNamespace + "/" + filename);
                }
            }
            else if (process.env.TRANSLATIONS_ROOT || process.env.REACT_APP_TRANSLATIONS_ROOT) {
                try {
                    localeTranslationFile = require((process.env.TRANSLATIONS_ROOT || process.env.REACT_APP_TRANSLATIONS_ROOT) + "/" + filename);
                }
                catch (e) {
                    console.warn("Failed to load translation file at path:\n             " + (process.env.TRANSLATIONS_ROOT || process.env.REACT_APP_TRANSLATIONS_ROOT) + "/" + filename);
                }
            }
            else {
                throw new Error('Missing environment variable TRANSLATIONS_ROOT or REACT_APP_TRANSLATIONS_ROOT to load translations');
            }
            return localeTranslationFile;
        },
        generateResourcesFromStrings: function (localizedStrings) {
            return Object.entries(localizedStrings).reduce(function (p, _a) {
                var _b;
                var locale = _a[0], localizedStringMap = _a[1];
                return __assign(__assign({}, p), (_b = {}, _b[locale] = {
                    translation: localizedStringMap,
                }, _b));
            }, {});
        },
        changeLocale: function (locale) {
            return (0, changeLocale_1.default)(i18nextInstance, locale).then(function () {
                window.location.reload();
            });
        },
        getCurrentLocale: function () {
            return i18nextInstance.language;
        },
        /**
         * Wrapper method that accounts for translations contained within
         * metadata objects (under a "value" property).
         *
         * IMPROVE: @brooks-dd Simplify translation method to lookup only by key
         * once translations are only generated as basic string key-value pairs
         * JIRA ticket: https://doordash.atlassian.net/browse/I18NL-235
         */
        translate: function (key, config) {
            return i18nextInstance.t([key + "." + translationMetadata_1.VALUE, key], config);
        }
    };
}
exports.default = i18nAppProvider;
